<template>
    <section>
        <Table
            bordered
            striped
            showFilter
            :hover="false"
            :isBusy="busy"
            :responsive="true"
            :fields="fields"
            :items="items"
            busyText="Lädt Anwesenheitsdaten..."
            emptyText="Keine Anwesenheitsdaten gefunden"
        >
            <template v-slot:dates="{ row: row }">
                <template v-if="row.item.dates.fields.length > 0">
                    <Table
                        tableClass="inner-table"
                        :bordered="false"
                        :striped="false"
                        :hover="false"
                        :fields="row.item.dates.fields"
                        :items="row.item.dates.items"
                    >
                        <template
                            v-for="col in row.item.dates.fields"
                            v-slot:[headConstraint(col.key)]="{ row: row }"
                        >
                            <div :key="col.key" class="custom-table-head-container">
                                <p class="date-field small">
                                    <b>
                                        {{
                                        $moment(
                                        row.field._info.datum
                                        ).format("DD.MM.Y")
                                        }}
                                    </b>
                                    <b-button
                                        size="sm"
                                        v-if="row.field._info.info"
                                        class="info-field-button"
                                        v-b-tooltip.hover
                                        :title="row.field._info.info"
                                    >
                                        <font-awesome-icon icon="info" />
                                    </b-button>
                                </p>
                            </div>
                        </template>

                        <template
                            v-for="col in row.item.dates.fields"
                            v-slot:[col.key]="{ row: row }"
                        >
                            <div :key="col.key">
                                <template v-if="isAvailable(row) === true">
                                    <font-awesome-icon icon="check" class="text-success" />
                                </template>
                                <template v-else-if="isAvailable(row) === false">
                                    <font-awesome-icon icon="times" class="text-danger" />
                                </template>
                                <template v-else>&nbsp;</template>
                            </div>
                        </template>
                    </Table>
                </template>
                <template v-else>&nbsp;</template>
            </template>

            <template v-slot:head(pflichtproben)="{ row: row }">
                <div class="custom-table-head-container">
                    <p>{{ row.label }}</p>
                    <p class="small">teilgenommen</p>
                    <p class="small text-secondary">benötigt (bis heute)</p>
                    <p class="small text-success">Differenz</p>
                </div>
            </template>

            <template v-slot:head(pflichtkonzerte)="{ row: row }">
                <div class="custom-table-head-container">
                    <p>{{ row.label }}</p>
                    <p class="small">teilgenommen</p>
                    <p class="small text-secondary">erfolgt (bis heute)</p>
                    <p class="small text-success">Differenz</p>
                </div>
            </template>

            <template v-slot:pflichtproben="{ row: row }">
                <Pflichtproben :data="row.item" prefix="probe" />
            </template>

            <template v-slot:pflichtkonzerte="{ row: row }">
                <Pflichtproben :data="row.item" prefix="konzert" />
            </template>
        </Table>
    </section>
</template>

<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import Table from "@/components/Table/Table";
import Pflichtproben from "@/components/Pflichtproben/Pflichtproben";

import "./MitgliederAnwesenheit.scss";

const AnwesenheitsverwaltungApi = ApiFactory.get("anwesenheitsverwaltung");

export default {
    name: "MitgliederAnwesenheit",
    props: ["id"],
    components: {
        Table,
        Pflichtproben
    },
    data() {
        return {
            fields: [
                {
                    key: "werk",
                    label: "Werk",
                    stickyColumn: true,
                    class: "table-light b-table-sticky-column"
                },
                {
                    key: "dates",
                    label: "Termine",
                    tdClass: "dates"
                },
                {
                    key: "pflichtproben",
                    label: "Pflichtproben",
                    class: "text-center",
                    tdClass: "probe",
                    thClass: "probe"
                },
                {
                    key: "pflichtkonzerte",
                    label: "Konzerte",
                    class: "text-center",
                    tdClass: "probe",
                    thClass: "probe"
                }
            ],
            items: [],
            busy: false
        };
    },
    watch: {
        id: {
            handler(val) {
                this.fetch();
            },
            immediate: true
        }
    },
    methods: {
        async fetch() {
            this.busy = true;
            if (this.id) {
                const response = await AnwesenheitsverwaltungApi.getAnwesenheitMitglied(
                    this.id
                );
                if (response.ok) {
                    const collection = response.json.collection;
                    for (var data of collection) {
                        var information = this.process(data);
                        if (data.probenplan) {
                            for (const date of data.probenplan) {
                                information.dates.fields.push({
                                    key: String(date.id),
                                    label: date.datum,
                                    class: "text-center",
                                    _info: {
                                        id: date.id,
                                        datum: date.datum,
                                        kuerzel: date.kuerzel,
                                        info: date.info,
                                        counterDa: date.proben_zaehler_da,
                                        counterHe: date.proben_zaehler_he
                                    }
                                });
                            }
                        }

                        this.items.push(information);
                    }
                }
            }
            this.busy = false;
        },
        process(data) {
            var item = {
                werk_id: data.werk.id,
                werk: data.werk.bezeichnung,
                werk_datum: data.werk.verfallsdatum,
                dates: {
                    fields: [],
                    items: []
                }
            };

            var dates = {};
            for (const date of data.mitglied.anwesenheit) {
                dates[date.termin_id] = {
                    termin_id: date.termin_id,
                    available: true
                };
            }
            item.dates.items.push(dates);

            item = Object.assign(item, {
                probe_active: data.mitglied.pflicht_proben.active,
                probe_until_now: data.mitglied.pflicht_proben.until_now_share,
                probe_diff: data.mitglied.pflicht_proben.diff,
                konzert_active: data.mitglied.pflicht_konzert.active,
                konzert_until_now:
                    data.mitglied.pflicht_konzert.until_now_share,
                konzert_diff: data.mitglied.pflicht_konzert.diff
            });
            return item;
        },
        headConstraint(key) {
            return `head(${key})`;
        },
        isAvailable(row) {
            if (row.field._info.id === row.value.termin_id) {
                return true;
            } else {
                if (
                    this.$moment(row.field._info.datum).diff(
                        this.$moment(),
                        "days"
                    ) < 0
                ) {
                    return false;
                } else {
                    return null;
                }
            }
        }
    }
};
</script>
